import { useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import Select, { components } from 'react-select'
import type { MultiValue } from 'react-select'

import { PositionMap } from '../../../../store/types'
import type { NegativeInfoDropdownOption } from '../../types'
import reactSelectStyles from '../filterConditionStyles'

type PositionsFilterDropdownProps<B extends boolean> = {
  name?: string
  defaultValue?: string // '1,2,3'
  onChange?: (item: any) => void
  onSubmit?: () => void
  isClearable?: boolean
  isCreatable?: boolean
  isMulti?: boolean
}

const CustomMultiValue: ComponentType<any> = (props: any) => {
  const isNegative = props.getValue()[props.index]?.isNegative
  const onButtonClick = (e: any) => {
    e.stopPropagation()

    props?.selectProps.setValues?.((values: any[]) => {
      const list = [...values]
      if (list[props.index].isNegative) {
        list[props.index].isNegative = false
      } else {
        list[props.index].isNegative = true
      }
      return list
    })
  }

  return (
    <components.MultiValue {...props}>
      <button
        type="button"
        onClick={onButtonClick}
        className={`${isNegative ? 'line-through' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {props.children}
      </button>
    </components.MultiValue>
  )
}

export default function PositionsFilterDropdown({
  name,
  defaultValue,
  onChange,
  onSubmit,
  isClearable = false,
}: PositionsFilterDropdownProps<boolean>) {
  const options = Object.keys(PositionMap).map((key) => ({
    label: PositionMap[Number(key)],
    value: key,
  }))
  const selectRef = useRef<any>()
  const [values, setValues] = useState<NegativeInfoDropdownOption[]>([])

  useMemo(() => {
    if (defaultValue !== undefined && defaultValue !== '') {
      try {
        const defaultValues = defaultValue
          ?.split(',')
          .map((i) => Number(i))
          .map((item) => {
            const value = Math.abs(item)
            const option = options.find((i) => i.value === value.toFixed(0))
            if (!option) {
              throw new Error()
            } else {
              if (item < 0) {
                return {
                  ...option!,
                  isNegative: true,
                }
              } else {
                return {
                  ...option!,
                  isNegative: false,
                }
              }
            }
          })

        if (defaultValues) {
          setValues(defaultValues)
        }
      } catch (err) {
        setValues([])
      }
    } else {
      setValues([])
    }
  }, [defaultValue])

  useEffect(() => {
    onChange?.(values)
  }, [values])

  return (
    <Select
      ref={selectRef}
      name={name as string}
      className="w-full text-sm font-semibold"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      isMulti={true}
      styles={reactSelectStyles}
      options={options}
      components={{
        MultiValue: CustomMultiValue,
      }}
      isSearchable={true}
      isClearable={isClearable}
      onChange={(opt: MultiValue<NegativeInfoDropdownOption>) => {
        const valueList = opt?.map((item) => {
          if (item.isNegative === undefined) {
            item.isNegative = false
          }
          return item
        })
        setValues(valueList)
      }}
      onKeyDown={(e: any) => {
        if (e.key === 'Enter' && onSubmit) {
          onSubmit()
          selectRef.current?.focus()
        }
      }}
      value={values || null}
      setValues={setValues}
    />
  )
}
